import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview
} from "../../../../components/inputs/FractionInput";

// ===== FORMATOWANIE UŁAMKÓW =====

function formatFraction(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const whole = Math.floor(abs);
  const decimal = abs - whole;

  const candidates = [
    { dec: 0.25, latex: "\\dfrac{1}{4}" },
    { dec: 0.3333, latex: "\\dfrac{1}{3}" },
    { dec: 0.5, latex: "\\dfrac{1}{2}" },
    { dec: 0.6666, latex: "\\dfrac{2}{3}" },
    { dec: 0.75, latex: "\\dfrac{3}{4}" },
  ];

  if (decimal < 0.01) return `${sign}${whole}`;

  const best = candidates.reduce((prev, curr) =>
    Math.abs(curr.dec - decimal) < Math.abs(prev.dec - decimal) ? curr : prev
  );

  if (whole === 0) return `${sign}${best.latex}`;

  return `${sign}${whole}${best.latex}`;
}


// ===== GENERATOR =====

export function generateTask() {

  const fractions = [
    0.25, 0.3333, 0.5, 0.6666, 0.75,
    1.25, 1.3333, 1.5, 1.6666, 1.75,
    2.25, 2.3333, 2.5, 2.6666, 2.75,
    3.25, 3.3333, 3.5, 3.6666, 3.75,
    4.25, 4.3333, 4.5, 4.6666, 4.75,
    5,
    -0.25, -0.3333, -0.5, -0.6666, -0.75,
    -1.25, -1.3333, -1.5, -1.6666, -1.75,
    -2.25, -2.3333, -2.5, -2.6666, -2.75,
    -3.25, -3.3333, -3.5, -3.6666, -3.75,
    -4.25, -4.3333, -4.5, -4.6666, -4.75,
    -5
  ];

  const a = fractions[Math.floor(Math.random() * fractions.length)];

  const x = Math.floor(Math.random() * 19 - 9);
  const y = Math.floor(Math.random() * 19 - 9);

  const b = parseFloat((y - a * x).toFixed(4));

  return {
    type: "LinearInterceptTask",
    data: { a, x, y, b }
  };
}


// ===== TREŚĆ ZADANIA =====

export function renderTask({ data }) {

  const { a, x, y } = data;

  return (
    <p>
      Funkcja liniowa <InlineMath math="f(x)=ax+b" /> ma współczynnik kierunkowy{" "}
      <InlineMath math={`a = ${formatFraction(a)}`} /> i przechodzi przez punkt{" "}
      <InlineMath math={`A = (${x}, ${y})`} />.  
      Oblicz wyraz wolny <InlineMath math="b" />.
    </p>
  );
}


// ===== INPUT =====

export function renderInput(_, userAnswer, onChange, disabled) {

  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="b"
    />
  );

}


// ===== SPRAWDZANIE =====

export function checkAnswer({ data }, userAnswer) {

  if (!userAnswer || typeof userAnswer !== "object") return false;

  const given = userAnswer.parsed;
  const expected = data.b;

  return given !== null && Math.abs(given - expected) < 0.01;

}


// ===== POPRAWNA ODPOWIEDŹ =====

export function renderAnswer({ data }) {

  const a = formatFraction(data.a);
  const b = formatFraction(data.b);

  const sign = data.b < 0 ? "- " + b.slice(1) : "+ " + b;

  return (
    <div className="space-y-4">

      <div>
        <InlineMath math={`b = ${b}`} />
      </div>

      <div>
        <strong>Wzór funkcji:</strong>
        <div style={{ marginTop: "0.5rem" }}>
          <InlineMath math={`\\displaystyle f(x) = ${a}x ${sign}`} />
        </div>
      </div>

    </div>
  );

}


// ===== WYJAŚNIENIE =====

export function renderExplanation({ data }) {

  const { a, x, y, b } = data;

  const axRaw = a * x;

  const axFormatted = formatFraction(axRaw);
  const bFormatted = formatFraction(b);
  const aFormatted = formatFraction(a);

  const parens = (val, formatted) =>
    val < 0 ? `(${formatted})` : formatted;

  return (
    <div className="space-y-2">

      <p><strong>Krok 1.</strong> Korzystamy ze wzoru funkcji liniowej:</p>

      <BlockMath math="f(x) = ax + b" />

      <p><strong>Krok 2.</strong> Przekształcamy wzór, aby obliczyć <InlineMath math="b" />:</p>

      <BlockMath math="b = y - ax" />

      <p><strong>Krok 3.</strong> Podstawiamy dane:</p>

      <BlockMath math={`b = ${y} - ${parens(a, aFormatted)} \\cdot ${x}`} />

      <BlockMath math={`b = ${y} - ${parens(axRaw, axFormatted)}`} />

      <BlockMath math={`b = ${bFormatted}`} />

    </div>
  );

}


// ===== ODPOWIEDŹ UCZNIA =====

export function renderUserAnswer(userAnswer) {

  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return <i>(brak odpowiedzi)</i>;

  return <InlineMath math={formatLatexPreview(userAnswer.raw, "b")} />;

}